import React from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import _ from 'lodash';

import styles from './Tooltip.scss';

const CONTAINER_CLASS = `.${styles.tooltipContainer}`;
/*
* This is for hot zones around the outer parameter of the screen.
* Upper Edge is the left-most and top-most edge of the window,
* Lower Edge is the right-most and bottom-most edge. This is
* so the tooltips know to reposition themselves if they're too close
* to the edge. Quadrant count determines how large the first and last
* "slices" of the window will be. The smaller the number, the larger
* the edge.
* */
const HALF = 2;
const QUADRANT_COUNT = 6;
const QUADRANT_SLICE = 1;
const UPPER_EDGE = QUADRANT_SLICE / QUADRANT_COUNT;
const LOWER_EDGE = (QUADRANT_COUNT - QUADRANT_SLICE) / QUADRANT_COUNT;

const activeTargets = {};

let container;

function handleClick(onDismiss) {
  return (ev) => {
    ev.stopPropagation();
    onDismiss();
  };
}

const renderTooltip = (message, color, size, position, arrow) => {
  container.classList.add(styles.active);

  const positionElm = (
    <TooltipFloat
      mouseEvent={event}
      colour={color}
      size={size}
      position={position}
      arrow={arrow}
    >
      {message}
    </TooltipFloat>
  );

  ReactDOM.render(positionElm, container);
};

const onMouseOver = (params) => {
  const {message, color, size, position, arrow} = params;
  renderTooltip(message, color, size, position, arrow);
};

const fadeOut = () => {
  container.classList.remove(styles.active);
};

const onMouseOut = () => {
  fadeOut();
};

const setMouseListeners = (target, selector, message, color, size, position, arrow) => {
  const mouseOverCallback = _.partial(onMouseOver, {target, message, color, size, position, arrow});
  target.addEventListener('mouseout', onMouseOut);
  target.addEventListener('mouseover', mouseOverCallback);
  activeTargets[selector] = mouseOverCallback;
};

const removeMouseListeners = (target, selector) => {
  if (target) {
    const mouseOverCallback = activeTargets[selector];
    target.removeEventListener('mouseout', onMouseOut);
    target.removeEventListener('mouseover', mouseOverCallback);
    activeTargets[selector] = null;
  }
};

const createContainer = () => {
  if (_.isEmpty(document.querySelectorAll(CONTAINER_CLASS))) {
    container = document.createElement('div');

    container.className = classnames([styles.tooltipContainer, styles.active]);

    document.body.appendChild(container);
  }
};

const attachTooltips = (tooltips) => {
  createContainer();

  tooltips.forEach((tip) => {
    const target = tip.target ? tip.target : document.querySelector(tip.selector);

    if (target !== null) {
      setMouseListeners(
        target,
        tip.selector,
        tip.copy,
        tip.color || 'blue',
        tip.size || 'small',
        tip.position || 'fixed',
        tip.arrow
      );
      target.classList.add('tooltip-added');
      if (tip.disabled) {
        target.classList.add(styles.disabled);
      }
    }
  });
};

const removeTooltips = (tooltips) => {
  tooltips.forEach((tip) => {
    const target = tip.target ? tip.target : document.querySelector(tip.selector);

    if (target !== null) {
      removeMouseListeners(target, tip.selector);
    }
  });
};

function TooltipContent(props) {
  const {arrow, bouncy, children, className, colour, onDismiss, size} = props;

  const classNames = classnames(
    styles.tooltip,
    styles[colour],
    styles[size],
    {
      [styles.bouncy]: bouncy,
      [styles.hasArrow]: arrow,
    },
    className
  );

  const closeButton = <button className={styles.closeButton} type='button' onClick={handleClick(onDismiss)} />;

  const close = props.hasClose ? closeButton : '';

  return (
    <div className={classNames} onMouseOut={onMouseOut}>
      <p className={styles.content}>{children}</p>
      {close}
    </div>
  );
}

class TooltipFloat extends React.Component {
  constructor(props) {
    super(props);
  }

  render() {
    const mouseEvent = this.props.mouseEvent;
    const target = this.props.mouseEvent.target;

    const setScreenOffset = () => {
      return {
        [mouseEvent.clientX > window.innerWidth / HALF ? 'right' : 'left']: 0,
        [mouseEvent.clientY > window.innerHeight / HALF ? 'bottom' : 'top']: 0
      };
    };

    const getScreenPosition = () => {
      return {
        top: this.props.mouseEvent.clientY,
        left: this.props.mouseEvent.clientX
      };
    };

    const getRelativeMousePos = () => {
      return {
        top: mouseEvent.clientY / window.innerHeight,
        left: mouseEvent.clientX / window.innerWidth,
      };
    };

    // eslint-disable-next-line complexity
    const getElementOffset = (anchor, size, {width, height}) => {
      const mousePos = getRelativeMousePos();
      const topHalf = mousePos.top > QUADRANT_SLICE / HALF;

      const elmObj = {
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        top: 0,
        left: 0,
        caretX: 'l',
        caretY: 't',
        width: size,
        height: size
      };

      // Calculate Vertical Position
      if (mousePos.top <= UPPER_EDGE) {
        _.assign(elmObj, {top: anchor.bottom.yP});
      } else if (mousePos.top > UPPER_EDGE && mousePos.top < LOWER_EDGE) {
        _.assign(elmObj, {
          alignItems: 'center',
          top: anchor.bottom.yP - (size / HALF),
          caretY: 'c'
        });
      } else {
        _.assign(elmObj, {
          alignItems: 'flex-end',
          top: anchor.top.yP - size,
          caretY: 'b'
        });
      }

      // Calculate Horizontal Position
      if (mousePos.left <= UPPER_EDGE) {
        _.assign(elmObj, {left: anchor.right.xP - (width / HALF)});
      } else if (mousePos.left > UPPER_EDGE && mousePos.left < LOWER_EDGE) {
        _.assign(elmObj, {
          justifyContent: 'center',
          left: anchor.right.xP - (size / HALF) - (width / HALF),
          caretX: 'c'
        });
      } else {
        _.assign(elmObj, {
          justifyContent: 'flex-end',
          left: anchor.left.xP - size + (width / HALF),
          caretX: 'r'
        });
      }

      // Center Cross Calculation
      // Vertical Center & Horizontal Left
      if (
        mousePos.left <= UPPER_EDGE &&
        mousePos.top > UPPER_EDGE && mousePos.top < LOWER_EDGE
      ) {
        _.assign(elmObj, {
          justifyContent: 'flex-start',
          left: anchor.right.xP,
          top: anchor.bottom.yP - (size / HALF) - (height / HALF),
          caretX: 'l',
          caretY: 'c'
        });
      }

      // Vertical Center & Horizontal Right
      if (
        mousePos.left >= LOWER_EDGE &&
        mousePos.top > UPPER_EDGE && mousePos.top < LOWER_EDGE
      ) {
        _.assign(elmObj, {
          left: anchor.left.xP - size,
          top: anchor.bottom.yP - (size / HALF) - (height / HALF),
          caretX: 'r',
          caretY: 'c'
        });
      }

      // CENTER CENTER
      if (
        mousePos.top > UPPER_EDGE && mousePos.top < LOWER_EDGE &&
        mousePos.left > UPPER_EDGE && mousePos.left < LOWER_EDGE
      ) {
        if (topHalf) {
          _.assign(elmObj, {
            alignItems: 'flex-end',
            top: anchor.bottom.yP - size - height,
            caretX: 'c',
            caretY: 'b'
          });
        } else {
          _.assign(elmObj, {
            alignItems: 'flex-start',
            top: anchor.top.yP + height,
            caretX: 'c',
            caretY: 't'
          });
        }
      }

      return {
        caret: `c-${elmObj.caretY}${elmObj.caretX}`,
        style: _.omit(elmObj, ['caretX', 'caretY'])
      };

    };

    const getFixedAnchor = () => {

      const containerSize = 350;
      const rect = target.getBoundingClientRect();

      const anchors = {
        top: {xP: rect.right - rect.left, yP: rect.top},
        bottom: {xP: rect.right - rect.left, yP: rect.bottom},
        left: {xP: rect.left, yP: rect.bottom - rect.top},
        right: {xP: rect.right, yP: rect.bottom - rect.top}
      };

      return getElementOffset(anchors, containerSize, rect);
    };

    const carets = [
      {placement: 'pl', points: '0,10 0,0 10,10 '},
      {placement: 'pc', points: '0,10 5,0 10,10 '},
      {placement: 'pr', points: '0,10 10,0 10,10 '}
    ];

    const caretElm = (
      <div className={styles.caretSVG}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 10">
          {carets.map(caret => {
            return (
              <polygon
                className={classnames(styles.poly, styles[caret.placement])}
                points={caret.points}
                key={`caret-${caret.placement}`}
              />
            );
          })}
        </svg>
      </div>
    );

    const tooltipContent = (
      <TooltipContent
        hover
        arrow={false}
        hasClose={false}
        bouncy={false}
        colour={this.props.colour}
        size={this.props.size}
      >
        {this.props.children}
      </TooltipContent>
    );

    const fixedAnchor = getFixedAnchor();

    const fixed = (
      <div className={styles.fixedScreenPlacement} style={fixedAnchor.style}>
        <div className={classnames(styles[fixedAnchor.caret], styles.caretAndTip)}>
          {this.props.arrow ? caretElm : ''}
          {tooltipContent}
        </div>
      </div>
    );

    const floating = (
      <div className={styles.floatScreenPlacement} style={getScreenPosition()}>
        <div className={styles.tipPosition} style={setScreenOffset()}>
          {this.props.arrow ? caretElm : ''}
          {tooltipContent}
        </div>
      </div>
    );

    return (this.props.position === 'fixed' ? fixed : floating);
  }
}

TooltipContent.propTypes = {
  arrow: React.PropTypes.bool,
  bouncy: React.PropTypes.bool,
  children: React.PropTypes.node.isRequired,
  colour: React.PropTypes.oneOf(['blue', 'yellow', 'white']),
  className: React.PropTypes.string,
  onDismiss: React.PropTypes.func,
  hasClose: React.PropTypes.bool,
  size: React.PropTypes.oneOf(['small', 'large']),
};

TooltipFloat.propTypes = {
  arrow: React.PropTypes.bool,
  target: React.PropTypes.object,
  children: React.PropTypes.node,
  mouseEvent: React.PropTypes.object,
  colour: React.PropTypes.oneOf(['blue', 'yellow', 'white']),
  size: React.PropTypes.oneOf(['small', 'large']),
  position: React.PropTypes.oneOf(['fixed', 'float']),
};

TooltipContent.defaultProps = {
  arrow: true,
  bouncy: true,
  colour: 'blue',
  hasClose: true,
  size: 'small'
};

TooltipFloat.defaultProps = {
  colour: 'white',
  size: 'large',
  position: 'float',
  arrow: true
};

export {TooltipContent, TooltipFloat, attachTooltips, removeTooltips};



// WEBPACK FOOTER //
// ./src/components/tooltip/components/Tooltip.jsx